import type { FleetMachine } from "@/context/fleet";
import { createJSONStorage, localStorageSource, sessionStorageSource } from "@/storage/browser";
import { readUserSessionToken } from "@/storage/user-session";

// Disposable caches of the account's fleet; the control plane is authoritative
// and every load revalidates them. The session copy keeps machine access
// grants for a few minutes. The durable copy has no credentials and outlives
// the browser session, so a returning device can show its machines' cached
// workspaces at once and renew access on first use.
const fleetCacheKey = "mux.fleet.snapshot.v1";
const durableFleetKey = "mux.fleet.machines.v1";
const ttl = 5 * 60_000;
let cached: FleetMachine[] | undefined;
let cachedAt = 0;
let cachedScope = "";

type StoredFleet = {
  scope: string;
  savedAt: number;
  machines: FleetMachine[];
};

const fleetStorage = createJSONStorage<StoredFleet | undefined>(fleetCacheKey, {
  decode(value) {
    if (!value || typeof value !== "object") return undefined;
    return value as StoredFleet;
  },
}, sessionStorageSource);

const durableStorage = createJSONStorage<StoredFleet | undefined>(durableFleetKey, {
  decode(value) {
    if (!value || typeof value !== "object" || !Array.isArray((value as StoredFleet).machines)) return undefined;
    return value as StoredFleet;
  },
}, localStorageSource);

function withoutCredentials(machine: FleetMachine): FleetMachine {
  const { auth: _auth, accessToken: _token, accessTokenExpiresAt: _expires, accessError: _error, ...rest } = machine;
  return rest;
}

function scope() {
  return readUserSessionToken();
}

function readStored(): StoredFleet | undefined {
  const value = fleetStorage.read(undefined);
  if (!value || value.scope !== scope() || Date.now() - value.savedAt > ttl) return undefined;
  return value;
}

// Only a signed-in account's copy, so one account never starts on another's machines.
function readDurable(): StoredFleet | undefined {
  const current = scope();
  const value = durableStorage.read(undefined);
  return current && value?.scope === current ? value : undefined;
}

function validAccess(machine: FleetMachine | undefined) {
  return !!(
    machine?.accessToken &&
    machine.accessTokenExpiresAt &&
    machine.accessTokenExpiresAt * 1000 - Date.now() > 120_000
  );
}

export function seedFleetSnapshot(machines: FleetMachine[]) {
  const existing = cachedScope === scope() ? cached : readStored()?.machines;
  const previous = new Map((existing ?? []).map((machine) => [machine.name, machine]));
  cached = machines.map((machine) => {
    const before = previous.get(machine.name);
    // An explicit access field is authoritative, including a cleared/rejected grant.
    if ("accessToken" in machine || !validAccess(before)) return machine;
    return {
      ...machine,
      accessToken: before?.accessToken,
      accessTokenExpiresAt: before?.accessTokenExpiresAt,
    };
  });
  cachedAt = Date.now();
  cachedScope = scope();
  fleetStorage.write({ scope: cachedScope, savedAt: cachedAt, machines: cached });
  if (cachedScope) durableStorage.write({ scope: cachedScope, savedAt: cachedAt, machines: cached.map(withoutCredentials) });
}

export function readFleetSnapshot() {
  const currentScope = scope();
  if (cached && cachedScope === currentScope && Date.now() - cachedAt <= ttl) return cached;
  const stored = readStored();
  if (!stored) return readDurable()?.machines;
  cached = stored.machines;
  cachedAt = stored.savedAt;
  cachedScope = stored.scope;
  return cached;
}

export function clearFleetSnapshot() {
  cached = undefined;
  cachedAt = 0;
  cachedScope = "";
  fleetStorage.remove();
  durableStorage.remove();
}
